/**
 * QuickOrderStatusUpdate Component
 * Provides a quick dropdown to update order status inline
 */

import React, { useState } from "react";
import { Select, Tag, message, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getOrderCompletionEligibility, updateOrderStatus } from "../services/orderService.js";
import CompleteOrderConfirmModal from "./CompleteOrderConfirmModal.jsx";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { Option } = Select;

const ORDER_STATUSES = [
  { value: "pending", label: "Pending", color: "orange" },
  { value: "booked", label: "Booked", color: "blue" },
  { value: "in_progress", label: "In Progress", color: "blue" },
  { value: "arrived_at_storage", label: "Arrived at Storage", color: "cyan" },
  { value: "in_transit_from_storage", label: "In Transit from Storage", color: "purple" },
  { value: "completed", label: "Completed", color: "green" },
  { value: "cancelled", label: "Cancelled", color: "red" },
];

/**
 * Get status color for Tag component
 */
const getStatusColor = (status) => {
  const statusOption = ORDER_STATUSES.find((s) => s.value === status);
  return statusOption?.color || "default";
};

/**
 * QuickOrderStatusUpdate Component
 * @param {Object} props
 * @param {Object} props.order - Order object
 * @param {Function} props.onStatusUpdate - Callback when status is updated (receives updated order)
 * @param {boolean} props.showLabel - Whether to show label text (default: true)
 * @param {boolean} props.allowSkipEmail - Whether to show option to skip email (default: false)
 * @param {string} props.size - Select size: 'small' | 'middle' | 'large' (default: 'small')
 */
const QuickOrderStatusUpdate = ({
  order,
  onStatusUpdate,
  showLabel = true,
  allowSkipEmail = false,
  size = "small",
}) => {
  const [updating, setUpdating] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(order?.status || "pending");
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [pendingSkipEmail, setPendingSkipEmail] = useState(false);

  if (!order) {
    return null;
  }

  const applyStatusChange = async (
    newStatus,
    { skipEmail = false, includeReviewRequest = false } = {}
  ) => {
    setUpdating(true);
    try {
      const context = {};
      if (skipEmail) context.skipEmail = true;
      if (newStatus === "completed") {
        context.includeReviewRequest = !!includeReviewRequest;
      }

      const updatedOrder = await updateOrderStatus(order.id, newStatus, context);

      logInfo("QuickOrderStatusUpdate: Status updated successfully", {
        orderId: order.id,
        oldStatus: currentStatus,
        newStatus: newStatus,
        skipEmail: skipEmail,
        includeReviewRequest: context.includeReviewRequest === true,
      });

      setCurrentStatus(newStatus);
      message.success(
        `Order status updated to ${ORDER_STATUSES.find((s) => s.value === newStatus)?.label || newStatus}`
      );

      if (onStatusUpdate) {
        onStatusUpdate({ ...order, status: newStatus });
      }
    } catch (error) {
      logError("QuickOrderStatusUpdate: Failed to update status", {
        orderId: order.id,
        newStatus: newStatus,
        error: error.message,
        stack: error.stack,
      });
      message.error(error.message || "Failed to update order status");
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (newStatus, skipEmail = false) => {
    if (newStatus === currentStatus) {
      return;
    }

    if (newStatus === "completed") {
      setUpdating(true);
      try {
        const eligibility = await getOrderCompletionEligibility(order.id);
        if (eligibility && eligibility.canComplete === false) {
          message.error(
            eligibility.message ||
              "Job completion blocked: resolve blockers first."
          );
          return;
        }
        setPendingSkipEmail(!!skipEmail);
        setCompleteConfirmOpen(true);
      } catch (error) {
        message.error(
          error.message || "Failed to validate completion eligibility"
        );
      } finally {
        setUpdating(false);
      }
      return;
    }

    await applyStatusChange(newStatus, { skipEmail });
  };

  const handleCompleteConfirm = async ({ includeReviewRequest }) => {
    setCompleteConfirmOpen(false);
    await applyStatusChange("completed", {
      skipEmail: pendingSkipEmail && allowSkipEmail,
      includeReviewRequest,
    });
    setPendingSkipEmail(false);
  };

  const statusOption = ORDER_STATUSES.find((s) => s.value === currentStatus);
  const displayLabel = statusOption?.label || currentStatus.replace("_", " ");

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: "8px" }}
      onClick={(e) => e.stopPropagation()}
    >
      {showLabel && (
        <Tag color={getStatusColor(currentStatus)} style={{ margin: 0 }}>
          {displayLabel.toUpperCase()}
        </Tag>
      )}
      <Select
        value={currentStatus}
        onChange={handleStatusChange}
        size={size}
        loading={updating}
        disabled={updating}
        style={{ minWidth: showLabel ? 140 : 160 }}
        suffixIcon={updating ? <LoadingOutlined /> : null}
      >
        {ORDER_STATUSES.map((status) => (
          <Option key={status.value} value={status.value}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tag color={status.color} style={{ margin: 0 }}>
                {status.label}
              </Tag>
            </div>
          </Option>
        ))}
      </Select>
      {updating && (
        <Tooltip title="Updating...">
          <LoadingOutlined style={{ color: "#1890ff" }} />
        </Tooltip>
      )}

      <CompleteOrderConfirmModal
        open={completeConfirmOpen}
        orderId={order.id}
        loading={updating}
        onConfirm={handleCompleteConfirm}
        onCancel={() => {
          setCompleteConfirmOpen(false);
          setPendingSkipEmail(false);
        }}
      />
    </div>
  );
};

export default QuickOrderStatusUpdate;
